import useDebounce from '@hooks/useDebounce'
import axios from 'axios'
import { backupCloudUserData } from 'helpers/cloudUserDataBackup'
import { mergeAndMigrateReadingTime } from 'helpers/readingTime'
import {
  applyUserDataChanges,
  getUserDataVersion,
  hasUserDataChanges,
} from 'helpers/userDataChanges'
import {
  clearUploadedUserDataChanges,
  migrateLegacyUserDataSyncState,
  normalizeUserDataRecord,
  readLocalUserData,
  readPendingUserDataChanges,
  writeLocalUserData,
} from 'helpers/userDataSyncState'
import clientApi from 'lib/clientApi'
import { auth } from '../config/firebase'
import { useUserDataRefresh } from './useUserDataRefresh'
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type Dispatch,
  type SetStateAction,
} from 'react'
import type { UserDataType } from '../types/UserDataType'

const USER_DATA_WRITE_URL = '/api/v2/storage'

type UserIdentity = {
  uid: string
  email?: string | null
  displayName?: string | null
} | null

type UseUserDataSyncOptions = {
  user: UserIdentity
  userLoading: boolean
  userData: UserDataType
  initialState: UserDataType
  setUserData: Dispatch<SetStateAction<UserDataType>>
  onDataLoaded?: (_data: UserDataType) => void | Promise<void>
}

/** Result of a cloud read; `fetched` is false when offline, signed out, or failed. */
type CloudReadResult = {
  fetched: boolean
  data: Partial<UserDataType> | null
}

/** Normalize legacy reading-time data before it is stored or exposed. */
const migrateUserDataReadingTime = (data: UserDataType) => {
  data.readingTime = mergeAndMigrateReadingTime(data.readingTime)
  return data
}

/** Return the signed-in user's Firebase ID token header, which `/api/storage` and `/api/v2/storage` require. */
const getAuthorizationHeader = async () => {
  const token = await auth.currentUser?.getIdToken()
  return token ? { Authorization: `Bearer ${token}` } : null
}

/**
 * Coordinate local persistence, cloud loading, uploads, and refreshes.
 *
 * The model: local edits are stored as pending changes with their edit time. The
 * shown record is always "base record + pending changes", where the base is the
 * cloud record when it could be read and the local copy otherwise. Uploads send
 * only the pending changes; the server merges them with "newest edit wins" per
 * field and returns the merged record.
 */
export const useUserDataSync = ({
  user,
  userLoading,
  userData,
  initialState,
  setUserData,
  onDataLoaded,
}: UseUserDataSyncOptions) => {
  const [loading, setLoading] = useState(true)
  const [initialLoadComplete, setInitialLoadComplete] = useState(false)
  // Uploads are allowed only after a successful authenticated cloud read.
  const [syncReady, setSyncReady] = useState(false)
  const debouncedUserData = useDebounce(userData, 1000)
  const isUserAuthenticated = Boolean(user?.uid)
  const loadInProgressRef = useRef(false)
  const uploadInProgressRef = useRef(false)
  const legacyStateMigratedRef = useRef(false)
  // `undefined` means "nothing backed up yet", so a record without any version
  // (version `null`) is still backed up once.
  const lastBackedUpVersionRef = useRef<string | null | undefined>(undefined)

  /** Normalize a partial record against defaults and the authenticated identity. */
  const normalizeUserData = useCallback(
    (data: Partial<UserDataType> | null) =>
      normalizeUserDataRecord(data, initialState, isUserAuthenticated ? user : null),
    [initialState, isUserAuthenticated, user],
  )

  /** Build the record to show: the base record with pending local edits applied. */
  const buildUserData = useCallback(
    (baseData: Partial<UserDataType> | null) =>
      migrateUserDataReadingTime(
        normalizeUserData(
          applyUserDataChanges(baseData || {}, readPendingUserDataChanges()),
        ),
      ),
    [normalizeUserData],
  )

  /** Save a record as the local copy and show it. */
  const storeAndShowUserData = useCallback(
    (data: UserDataType) => {
      writeLocalUserData(data)
      setUserData(data)
    },
    [setUserData],
  )

  /** Read the cloud record of the signed-in user, identified by their ID token. */
  const readCloudUserData = useCallback(async (): Promise<CloudReadResult> => {
    if (!isUserAuthenticated || !navigator.onLine) return { fetched: false, data: null }

    try {
      const authorizationHeader = await getAuthorizationHeader()
      // Without a token the server cannot identify the user, so the read fails;
      // uploads stay disabled until a later read succeeds.
      if (!authorizationHeader) return { fetched: false, data: null }

      const response = await clientApi.get('/storage', { headers: authorizationHeader })
      return { fetched: true, data: response.data || null }
    } catch (error) {
      console.error('Error fetching cloud data:', error)
      return { fetched: false, data: null }
    }
  }, [isUserAuthenticated])

  /** Back up each cloud version once before it is merged into the shown record. */
  const backupCloudVersion = useCallback(
    async (cloudData: Partial<UserDataType> | null) => {
      // Each cloud version is backed up only once.
      const version = getUserDataVersion(cloudData)
      if (!cloudData || version === lastBackedUpVersionRef.current) return

      lastBackedUpVersionRef.current = version
      await backupCloudUserData(cloudData, user!.uid, 'before-merge')
    },
    [user],
  )

  /** Upload pending local edits and show the merged record the server returns. */
  const uploadPendingChanges = useCallback(async () => {
    if (!isUserAuthenticated || !syncReady || !navigator.onLine) return
    // Never overlap a read and an upload (see `loadUserData`).
    if (uploadInProgressRef.current || loadInProgressRef.current) return

    const changes = readPendingUserDataChanges()
    if (!hasUserDataChanges(changes)) return

    uploadInProgressRef.current = true
    try {
      const authorizationHeader = await getAuthorizationHeader()
      // Without a token the server cannot identify the user; retry on a later trigger.
      if (!authorizationHeader) return

      const response = await axios.post(
        USER_DATA_WRITE_URL,
        { changes },
        { headers: authorizationHeader },
      )
      clearUploadedUserDataChanges(changes)
      // This version was written by this device, so a later read of it is not a
      // cloud change worth a backup. Backing it up would push older backups out.
      lastBackedUpVersionRef.current = getUserDataVersion(response.data)
      // Edits made during the upload are still pending and are applied on top.
      storeAndShowUserData(buildUserData(response.data))
    } catch (error) {
      console.error('Error syncing user data:', error)
    } finally {
      uploadInProgressRef.current = false
    }
  }, [buildUserData, isUserAuthenticated, storeAndShowUserData, syncReady])

  /** Show the local copy while authentication is still loading on an offline device. */
  const loadOfflineUserDataDuringAuth = useCallback(async () => {
    const offlineData = buildUserData(readLocalUserData())
    await onDataLoaded?.(offlineData)
    setUserData(offlineData)
    setLoading(false)
    setInitialLoadComplete(true)
  }, [buildUserData, onDataLoaded, setUserData])

  /** Load the cloud (or local) record, apply pending edits, and show the result. */
  const loadUserData = useCallback(
    async (silent: boolean) => {
      if (loadInProgressRef.current) return
      // A background read that overlaps an upload could return the record from
      // before the upload and show it after the upload's newer response. Skip it;
      // the next refresh runs within 30 seconds. Uploads do not start during a read.
      if (silent && uploadInProgressRef.current) return
      loadInProgressRef.current = true

      if (!silent) {
        setLoading(true)
        setInitialLoadComplete(false)
      }
      setSyncReady(false)

      try {
        if (userLoading) {
          if (!navigator.onLine && !silent) await loadOfflineUserDataDuringAuth()
          return
        }

        const cloud = await readCloudUserData()
        // Old local sync state can only be judged once the cloud record is known.
        // It is migrated once; later loads (including 30-second refreshes) skip it.
        if (!legacyStateMigratedRef.current) {
          legacyStateMigratedRef.current = migrateLegacyUserDataSyncState(
            cloud.fetched ? cloud.data : undefined,
          )
        }
        if (cloud.fetched) await backupCloudVersion(cloud.data)

        // The cloud record is the base when it exists. The local copy is used when
        // the cloud cannot be read, or for an account without a cloud record yet.
        const baseData = cloud.data || readLocalUserData()
        const loadedData = buildUserData(baseData)

        await onDataLoaded?.(loadedData)
        storeAndShowUserData(loadedData)
        if (cloud.fetched) setSyncReady(true)
        if (!silent) {
          setLoading(false)
          setInitialLoadComplete(true)
        }
      } catch (error) {
        console.error('Error loading user data:', error)
        if (!silent) {
          setLoading(false)
          setInitialLoadComplete(true)
        }
      } finally {
        loadInProgressRef.current = false
      }
    },
    [
      backupCloudVersion,
      buildUserData,
      loadOfflineUserDataDuringAuth,
      onDataLoaded,
      readCloudUserData,
      storeAndShowUserData,
      userLoading,
    ],
  )

  const getAndSetUserData = useCallback(() => loadUserData(false), [loadUserData])

  useEffect(() => {
    void getAndSetUserData()
  }, [getAndSetUserData])

  // Upload after edits settle, and again whenever uploads become allowed.
  useEffect(() => {
    if (initialLoadComplete) void uploadPendingChanges()
  }, [debouncedUserData, initialLoadComplete, uploadPendingChanges])

  const refreshUserDataFromCloud = useCallback(
    () => void loadUserData(true),
    [loadUserData],
  )
  useUserDataRefresh({
    enabled: initialLoadComplete && isUserAuthenticated,
    refresh: refreshUserDataFromCloud,
  })

  return { loading, getAndSetUserData }
}
